import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import FormTabs, { FormTab } from '../common/FormTabs';
import DateTimePicker from '../common/DateTimePicker';
import TimePicker from '../common/TimePicker';
import { Worksheet, CreateWorksheetData } from '../../types/worksheet';
import {
  ClipboardDocumentListIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  TruckIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../../hooks/useCustomers';
import { useLanguage } from '../../contexts/LanguageContext';
import { Combobox } from '@headlessui/react';

interface WorksheetFormProps {
  onSubmit: (data: CreateWorksheetData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Worksheet | null;
}

interface WorksheetFormData {
  title: string;
  description?: string;
  customerId: string;
  workType: 'installation' | 'maintenance' | 'repair' | 'inspection' | 'other';
  priority: 'low' | 'medium' | 'high';
  
  // Dátumok és időzítés
  dueDate?: Date | null;
  worksheetDate?: Date | null;
  startTime?: string;
  endTime?: string;
  
  // Kiszállási adatok
  deploymentOccurred?: boolean;
  deploymentDate?: Date | null;
  deploymentTime?: string;
  
  // Utazási adatok
  travelKmStart?: number;
  travelKmEnd?: number;
  travelKmTotal?: number;
  travelCosts?: number;
  
  // Munka részletei
  estimatedHours?: number;
  location?: string;
  workDetails?: string;
  materials?: string;
  equipment?: string;
  safetyNotes?: string;
  
  // Egyéb
  specialNotes?: string;
}

const WorksheetForm: React.FC<WorksheetFormProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { customers } = useCustomers();
  const { t } = useLanguage();
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [customerQuery, setCustomerQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'basic' | 'work' | 'deployment'>('basic');

  const TABS: FormTab[] = [
    { id: 'basic', label: t('worksheets.form.tabs.basic'), icon: ClipboardDocumentListIcon },
    { id: 'work', label: t('worksheets.form.tabs.work'), icon: WrenchScrewdriverIcon },
    { id: 'deployment', label: t('worksheets.form.tabs.deployment'), icon: TruckIcon },
  ];
  const panel = (id: typeof activeTab) => (activeTab === id ? 'space-y-5' : 'hidden');

  const defaultValues: WorksheetFormData = {
    title: '',
    description: '',
    customerId: '',
    workType: 'installation',
    priority: 'medium',
    
    // Dátumok és időzítés
    dueDate: null,
    worksheetDate: null,
    startTime: '',
    endTime: '',
    
    // Kiszállási adatok
    deploymentOccurred: false,
    deploymentDate: null,
    deploymentTime: '',
    
    // Utazási adatok
    travelKmStart: 0,
    travelKmEnd: 0,
    travelKmTotal: 0,
    travelCosts: 0,
    
    // Munka részletei
    estimatedHours: 0,
    location: '',
    workDetails: '',
    materials: '',
    equipment: '',
    safetyNotes: '',
    
    // Egyéb
    specialNotes: ''
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<WorksheetFormData>({
    defaultValues: initialData || defaultValues
  });

  // Register customerId field for validation
  React.useEffect(() => {
    register('customerId', { required: true });
  }, [register]);

  // Update form when customer is selected
  React.useEffect(() => {
    setValue('customerId', selectedCustomer);
  }, [selectedCustomer, setValue]);

  useEffect(() => {
    if (isOpen && initialData) {
      // Convert date to proper format for form
      const formData = {
        ...initialData,
        dueDate: initialData.dueDate ? new Date(initialData.dueDate) : null
      };
      reset(formData);
      setSelectedCustomer(initialData.customerId);
    } else if (isOpen && !initialData) {
      reset(defaultValues);
      setSelectedCustomer('');
      setCustomerQuery('');
    }
  }, [initialData, reset, isOpen]);

  const filteredCustomers = customers.filter(customer => {
    if (!customerQuery.trim()) return true;
    
    const query = customerQuery.toLowerCase();
    const nameToMatch = customer.type === 'organization'
      ? customer.companyName.toLowerCase()
      : `${customer.lastName} ${customer.firstName}`.toLowerCase();
    
    return nameToMatch.includes(query) || 
           customer.email?.toLowerCase().includes(query) ||
           customer.phone?.toLowerCase().includes(query);
  });

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'maintenance': return <CogIcon className="w-5 h-5" />;
      case 'repair': return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'inspection': return <MagnifyingGlassIcon className="w-5 h-5" />;
      default: return <ClipboardDocumentListIcon className="w-5 h-5" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'medium': return <ClockIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
      case 'low': return <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
      default: return <ClockIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const onSubmitHandler = (data: WorksheetFormData) => {
    const deploymentOccurred = data.deploymentOccurred || false;
    const submitData: CreateWorksheetData = {
      ...data,
      customerId: selectedCustomer,
      status: 'draft',
      dueDate: data.dueDate || undefined,
      worksheetDate: data.worksheetDate || undefined,
      deploymentDate: deploymentOccurred ? (data.deploymentDate || undefined) : undefined,
      deploymentOccurred,
      deploymentTime: deploymentOccurred ? (data.deploymentTime || '') : '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      travelKmStart: deploymentOccurred ? (data.travelKmStart ?? 0) : 0,
      travelKmEnd: deploymentOccurred ? (data.travelKmEnd ?? 0) : 0,
      travelKmTotal: deploymentOccurred ? (data.travelKmTotal ?? 0) : 0,
      travelCosts: deploymentOccurred ? (data.travelCosts ?? 0) : 0,
      specialNotes: data.specialNotes || '',
    };
    onSubmit(submitData);
  };

  const handleModalClose = () => {
    setActiveTab('basic');
    reset(defaultValues);
    setSelectedCustomer('');
    setCustomerQuery('');
    onCancel();
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={initialData ? t('worksheets.edit') : t('worksheets.new')}
      size="4xl"
      noPadding
    >
      <form onSubmit={handleSubmit(onSubmitHandler, () => setActiveTab('basic'))} noValidate className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 flex-1 pb-5">
        <div className="sticky top-0 z-10 bg-[var(--crm-surface)] pt-3 sm:pt-4">
          <FormTabs sticky={false} tabs={TABS} active={activeTab} onChange={(id) => setActiveTab(id as typeof activeTab)} errorTabs={errors.title ? ['basic'] : []} />
        </div>
        <div className="pt-5">
        <div className={panel('basic')}>
        {/* Basic Worksheet Info Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <ClipboardDocumentListIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.title')}</h3>
          </div>
          <div className="p-4">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.title')} *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: true })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.title')}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{t('common.validation.required')}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.description')}
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.description')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Selection Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.customer')}</h3>
          </div>
          <div className="p-4">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.customer')} *
                  </label>
                  
                  <Combobox value={selectedCustomer} onChange={setSelectedCustomer}>
                    <div className="relative">
                      <Combobox.Input
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          return customer ? (
                            customer.type === 'organization' 
                              ? customer.companyName 
                              : `${customer.firstName} ${customer.lastName}`
                          ) : '';
                        }}
                        onChange={(event) => setCustomerQuery(event.target.value)}
                        placeholder={t('common.placeholders.selectCustomer')}
                      />
                      <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {filteredCustomers.length === 0 && customerQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                            {t('common.noData')}
                          </div>
                        ) : (
                          filteredCustomers.map((customer) => (
                            <Combobox.Option
                              key={customer.id}
                              value={customer.id}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                  active ? 'bg-primary-600 text-white' : 'text-gray-900 dark:text-gray-200'
                                }`
                              }
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 mr-3">
                                  {customer.type === 'organization' ? (
                                    <BuildingOffice2Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                  ) : (
                                    <UserIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                  )}
                                </div>
                                <div className="block truncate">
                                  {customer.type === 'organization' 
                                    ? customer.companyName 
                                    : `${customer.firstName} ${customer.lastName}`}
                                </div>
                              </div>
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                  {!selectedCustomer && errors.customerId && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{t('common.validation.required')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.location')}
                  </label>
                  <input
                    type="text"
                    {...register('location')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.location')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.estimatedHours')}
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    {...register('estimatedHours', { valueAsNumber: true })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        <div className={panel('work')}>
        {/* Work Type and Priority Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <WrenchScrewdriverIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.workType')}</h3>
          </div>
          <div className="p-4">
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.workType')} *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { value: 'installation', label: t('worksheets.workTypes.installation') },
                      { value: 'maintenance', label: t('worksheets.workTypes.maintenance') },
                      { value: 'repair', label: t('worksheets.workTypes.repair') },
                      { value: 'inspection', label: t('worksheets.workTypes.inspection') }
                    ].map((type) => (
                      <label key={type.value} className="relative flex items-center group">
                        <input
                          type="radio"
                          value={type.value}
                          {...register('workType')}
                          className="sr-only peer"
                        />
                        <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                      peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                      hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200 bg-white dark:bg-gray-700">
                          {getWorkTypeIcon(type.value)}
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-200 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                            {type.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.priority')}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'low', label: t('worksheets.priority.low') },
                      { value: 'medium', label: t('worksheets.priority.medium') },
                      { value: 'high', label: t('worksheets.priority.high') }
                    ].map((priority) => (
                      <label key={priority.value} className="relative flex items-center group">
                        <input
                          type="radio"
                          value={priority.value}
                          {...register('priority')}
                          className="sr-only peer"
                        />
                        <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                      peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                      hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200 bg-white dark:bg-gray-700">
                          {getPriorityIcon(priority.value)}
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-200 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                            {priority.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.dueDate')}
                  </label>
                  <DateTimePicker showTime={false} value={watch('dueDate') || undefined} onChange={(d) => setValue('dueDate', d || null)} placeholder="Válassz dátumot" />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[
                      { label: t('worksheets.quickDates.day1'), days: 1 },
                      { label: t('worksheets.quickDates.day3'), days: 3 },
                      { label: t('worksheets.quickDates.day5'), days: 5 },
                      { label: t('worksheets.quickDates.day8'), days: 8 },
                      { label: t('worksheets.quickDates.week1'), days: 7 },
                      { label: t('worksheets.quickDates.week2'), days: 14 },
                      { label: t('worksheets.quickDates.month1'), days: 30 },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => {
                          const newDate = new Date();
                          newDate.setDate(newDate.getDate() + preset.days);
                          setValue('dueDate', newDate);
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setValue('dueDate', null)}
                      className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Törlés
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Work Details Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.workDetails')}</h3>
          </div>
          <div className="p-4">
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.workDetails')}
                  </label>
                  <textarea
                    {...register('workDetails')}
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.workDetails')}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('worksheets.fields.materials')}
                    </label>
                    <textarea
                      {...register('materials')}
                      rows={3}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('worksheets.fields.materials')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('worksheets.fields.equipment')}
                    </label>
                    <textarea
                      {...register('equipment')}
                      rows={3}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('worksheets.fields.equipment')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.safetyNotes')}
                  </label>
                  <textarea
                    {...register('safetyNotes')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.safetyNotes')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        <div className={panel('deployment')}>
        {/* Deployment and Travel Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TruckIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.deploymentAndTravel')}</h3>
          </div>
          <div className="p-4">
              
              <div className="space-y-4">
                {/* Worksheet Date and Times */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('worksheets.fields.worksheetDate')}
                    </label>
                    <DateTimePicker showTime={false} value={watch('worksheetDate') || undefined} onChange={(d) => setValue('worksheetDate', d || null)} placeholder="Válassz dátumot" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Munka kezdete
                    </label>
                    <TimePicker value={watch('startTime') || ''} onChange={(v) => setValue('startTime', v)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Munka vége
                    </label>
                    <TimePicker value={watch('endTime') || ''} onChange={(v) => setValue('endTime', v)} />
                  </div>
                </div>

                {/* Deployment Section */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('deploymentOccurred')}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-700"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('worksheets.fields.deploymentOccurred')}</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('worksheets.fields.deploymentDate')}
                      </label>
                      <DateTimePicker showTime={false} value={watch('deploymentDate') || undefined} onChange={(d) => setValue('deploymentDate', d || null)} placeholder="Válassz dátumot" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('worksheets.fields.deploymentTime')}
                      </label>
                      <TimePicker value={watch('deploymentTime') || ''} onChange={(v) => setValue('deploymentTime', v)} />
                    </div>
                  </div>
                </div>

                {/* Travel Data Section */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Km óra állás (indulás)
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmStart', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Km óra állás (érkezés)
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmEnd', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('worksheets.fields.totalKm')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmTotal', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('worksheets.fields.travelCostFt')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelCosts', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('worksheets.fields.additionalInfo')}</h3>
          </div>
          <div className="p-4">
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.specialNotes')}
                  </label>
                  <textarea
                    {...register('specialNotes')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.additionalNotesPlaceholder')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        </div>
        </div>
        {/* Form Actions */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
          <button
            type="button"
            onClick={handleModalClose}
            className="crm-btn crm-btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="crm-btn crm-btn-primary"
          >
            {initialData ? t('common.save') : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WorksheetForm; 